<template>
  <ContentWrap title="酒店管理 PC 端设计">
    <div class="pc-diy">
      <div class="toolbar">
        <div>
          <div class="page-title">酒店 PC 端 DIY</div>
          <div class="page-desc">编辑 PC 官网预订页组件，保存后可生成模板配置。</div>
        </div>
        <div class="toolbar-actions">
          <el-button @click="resetTemplate">重置</el-button>
          <el-button @click="generateTemplate">生成模板</el-button>
          <el-button type="primary" @click="saveTemplate">保存</el-button>
        </div>
      </div>

      <div class="editor-shell">
        <aside class="library">
          <div class="side-title">组件库</div>
          <button
            v-for="item in componentLibrary"
            :key="item.type"
            class="library-item"
            @click="addComponent(item.type)"
          >
            <span>{{ item.icon }}</span>
            <strong>{{ item.name }}</strong>
          </button>
        </aside>

        <main class="canvas">
          <div
            v-for="(item, index) in pageComponents"
            :key="item.uid"
            class="canvas-block"
            :class="{ active: selectedIndex === index }"
            @click="selectComponent(index)"
          >
            <section v-if="item.type === 'hero'" class="hero" :style="{ backgroundImage: `url(${item.image})` }">
              <div class="hero-mask">
                <div class="eyebrow">{{ item.kicker }}</div>
                <h1>{{ item.title }}</h1>
                <p>{{ item.desc }}</p>
              </div>
            </section>

            <section v-else-if="item.type === 'search'" class="search-card">
              <div v-for="field in ['目的地', '入住', '离店', '住客']" :key="field">
                <span>{{ field }}</span>
                <strong>{{ field === '目的地' ? item.city : field === '住客' ? item.guest : '选择日期' }}</strong>
              </div>
              <button>{{ item.buttonText }}</button>
            </section>

            <section v-else-if="item.type === 'rooms'" class="rooms">
              <div class="section-title">{{ item.title }}</div>
              <div class="room-grid">
                <div v-for="room in item.rooms" :key="room.name" class="room-card">
                  <div class="room-image" :style="{ backgroundImage: `url(${room.image})` }"></div>
                  <strong>{{ room.name }}</strong>
                  <span>{{ room.desc }}</span>
                  <b>¥{{ room.price }}</b>
                </div>
              </div>
            </section>

            <section v-else-if="item.type === 'benefits'" class="benefits">
              <div class="section-title">{{ item.title }}</div>
              <div class="benefit-grid">
                <div v-for="benefit in item.items" :key="benefit" class="benefit-card">{{ benefit }}</div>
              </div>
            </section>
          </div>
        </main>

        <aside class="props">
          <div class="side-title">属性编辑</div>
          <template v-if="selectedComponent">
            <el-form label-position="top" class="prop-form">
              <el-form-item label="组件名称">
                <el-input v-model="selectedComponent.name" />
              </el-form-item>
              <template v-if="selectedComponent.type === 'hero'">
                <el-form-item label="眉标"><el-input v-model="selectedComponent.kicker" /></el-form-item>
                <el-form-item label="主标题"><el-input v-model="selectedComponent.title" /></el-form-item>
                <el-form-item label="描述"><el-input v-model="selectedComponent.desc" type="textarea" /></el-form-item>
                <el-form-item label="背景图"><el-input v-model="selectedComponent.image" /></el-form-item>
              </template>
              <template v-else-if="selectedComponent.type === 'search'">
                <el-form-item label="默认城市"><el-input v-model="selectedComponent.city" /></el-form-item>
                <el-form-item label="住客文案"><el-input v-model="selectedComponent.guest" /></el-form-item>
                <el-form-item label="按钮文案"><el-input v-model="selectedComponent.buttonText" /></el-form-item>
              </template>
              <template v-else-if="selectedComponent.type === 'rooms'">
                <el-form-item label="区块标题"><el-input v-model="selectedComponent.title" /></el-form-item>
                <el-form-item label="房型数量">
                  <el-input-number v-model="selectedComponent.roomCount" :min="1" :max="6" @change="syncRooms" />
                </el-form-item>
              </template>
              <template v-else-if="selectedComponent.type === 'benefits'">
                <el-form-item label="区块标题"><el-input v-model="selectedComponent.title" /></el-form-item>
                <el-form-item label="权益文案">
                  <el-input v-model="benefitText" type="textarea" @change="syncBenefits" />
                </el-form-item>
              </template>
            </el-form>
            <div class="prop-actions">
              <el-button :disabled="selectedIndex <= 0" @click="moveComponent(-1)">上移</el-button>
              <el-button :disabled="selectedIndex === pageComponents.length - 1" @click="moveComponent(1)">下移</el-button>
              <el-button type="danger" plain @click="deleteComponent">删除</el-button>
            </div>
          </template>
          <el-empty v-else description="请选择画布组件" :image-size="80" />
        </aside>
      </div>
    </div>

    <Dialog v-model="templateDialogVisible" title="PC 模板配置" width="760px">
      <el-input :model-value="templateJson" type="textarea" :rows="18" readonly />
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'

defineOptions({ name: 'HotelPcDesign' })

type ComponentType = 'hero' | 'search' | 'rooms' | 'benefits'
type PcComponent = Record<string, any> & { uid: number; type: ComponentType; name: string }

const message = useMessage()
const storageKey = 'hotel-pc-diy-template'

const componentLibrary = [
  { type: 'hero' as ComponentType, name: '首屏横幅', icon: 'H' },
  { type: 'search' as ComponentType, name: '预订搜索', icon: 'S' },
  { type: 'rooms' as ComponentType, name: '房型展示', icon: 'R' },
  { type: 'benefits' as ComponentType, name: '会员权益', icon: 'V' }
]

const defaultTemplate = [
  {
    uid: 1,
    type: 'hero',
    name: '首屏横幅',
    kicker: 'ZJS HOTEL',
    title: '城市度假与商务出行的一站式预订体验',
    desc: '整合房型展示、价格日历、会员礼遇与门店服务，帮助酒店快速搭建 PC 官网预订页。',
    image: 'https://picsum.photos/seed/hotel-pc-hero/1280/560'
  },
  { uid: 2, type: 'search', name: '预订搜索', city: '南京 · 玄武区', guest: '2成人 1间房', buttonText: '搜索房型' },
  {
    uid: 3,
    type: 'rooms',
    name: '房型展示',
    title: '精选房型',
    roomCount: 3,
    rooms: [
      { name: '豪华大床房', desc: '42m2 / 含早餐', price: 688, image: 'https://picsum.photos/seed/hotel-room-a/320/220' },
      { name: '行政双床房', desc: '48m2 / 城市景观', price: 828, image: 'https://picsum.photos/seed/hotel-room-b/320/220' },
      { name: '亲子套房', desc: '68m2 / 儿童设施', price: 1088, image: 'https://picsum.photos/seed/hotel-room-c/320/220' }
    ]
  },
  { uid: 4, type: 'benefits', name: '会员权益', title: '会员礼遇', items: ['14:00 延迟退房', '入住欢迎水果', '会员价直减', '积分兑换房券'] }
] as PcComponent[]

const pageComponents = ref<PcComponent[]>([])
const selectedIndex = ref(0)
const templateDialogVisible = ref(false)
const templateJson = ref('')

const selectedComponent = computed(() => pageComponents.value[selectedIndex.value])
const benefitText = computed({
  get: () => (selectedComponent.value?.items || []).join('\n'),
  set: (value: string) => {
    if (selectedComponent.value) {
      selectedComponent.value.items = value.split('\n').filter(Boolean)
    }
  }
})

const createComponent = (type: ComponentType) => {
  const source = defaultTemplate.find((item) => item.type === type) || defaultTemplate[0]
  return { ...cloneDeep(source), uid: Date.now() }
}

const loadTemplate = () => {
  const saved = localStorage.getItem(storageKey)
  pageComponents.value = saved ? JSON.parse(saved) : cloneDeep(defaultTemplate)
}

const saveTemplate = () => {
  localStorage.setItem(storageKey, JSON.stringify(pageComponents.value))
  message.success('保存成功')
}

const resetTemplate = () => {
  pageComponents.value = cloneDeep(defaultTemplate)
  selectedIndex.value = 0
}

const generateTemplate = () => {
  templateJson.value = JSON.stringify(
    {
      type: 'hotel-pc',
      version: '1.0.0',
      components: pageComponents.value
    },
    null,
    2
  )
  templateDialogVisible.value = true
}

const addComponent = (type: ComponentType) => {
  pageComponents.value.push(createComponent(type))
  selectedIndex.value = pageComponents.value.length - 1
}

const selectComponent = (index: number) => {
  selectedIndex.value = index
}

const moveComponent = (direction: number) => {
  const nextIndex = selectedIndex.value + direction
  if (nextIndex < 0 || nextIndex >= pageComponents.value.length) return
  ;[pageComponents.value[selectedIndex.value], pageComponents.value[nextIndex]] = [
    pageComponents.value[nextIndex],
    pageComponents.value[selectedIndex.value]
  ]
  selectedIndex.value = nextIndex
}

const deleteComponent = () => {
  pageComponents.value.splice(selectedIndex.value, 1)
  selectedIndex.value = Math.max(0, selectedIndex.value - 1)
}

const syncRooms = () => {
  const component = selectedComponent.value
  if (!component || component.type !== 'rooms') return
  const count = component.roomCount || 1
  while (component.rooms.length < count) {
    component.rooms.push({
      name: `舒适房型 ${component.rooms.length + 1}`,
      desc: '可编辑房型描述',
      price: 588,
      image: `https://picsum.photos/seed/hotel-room-${Date.now()}/320/220`
    })
  }
  component.rooms = component.rooms.slice(0, count)
}

const syncBenefits = () => {
  benefitText.value = benefitText.value
}

onMounted(loadTemplate)
</script>

<style scoped lang="scss">
.pc-diy {
  display: grid;
  gap: 16px;
}

.toolbar,
.editor-shell,
.library,
.props,
.canvas {
  border: 1px solid #e8ecf2;
  border-radius: 8px;
  background: #fff;
}

.toolbar {
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  background:
    linear-gradient(90deg, rgba(47, 128, 237, 0.08), rgba(0, 166, 125, 0.06)),
    #fff;
}

.page-title {
  font-size: 18px;
  font-weight: 700;
  color: #202532;
}

.page-desc {
  margin-top: 4px;
  font-size: 13px;
  color: #687386;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.editor-shell {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) 320px;
  gap: 16px;
  padding: 16px;
  background:
    linear-gradient(90deg, rgba(47, 128, 237, 0.04), rgba(0, 166, 125, 0.04)),
    #f6f8fb;
}

.library,
.props {
  padding: 16px;
  align-self: start;
  position: sticky;
  top: 12px;
}

.side-title {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #202532;
}

.library-item {
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 8px;
  background: #f7f9fc;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  margin-bottom: 10px;
  cursor: pointer;
  color: #3a4556;
  transition: background 0.2s, transform 0.2s;
}

.library-item:hover {
  background: #eef6ff;
  transform: translateX(2px);
}

.library-item span {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #eef6ff;
  color: #2f80ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.canvas {
  padding: 18px;
  min-height: 720px;
  background:
    linear-gradient(transparent 31px, rgba(232, 236, 242, 0.55) 32px),
    linear-gradient(90deg, transparent 31px, rgba(232, 236, 242, 0.55) 32px),
    #fff;
  background-size: 32px 32px;
}

.canvas-block {
  border: 1px solid transparent;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 42, 61, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.canvas-block:hover {
  border-color: #b8d8ff;
  transform: translateY(-1px);
}

.canvas-block.active {
  border-color: #2f80ed;
  box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.12), 0 14px 34px rgba(31, 42, 61, 0.12);
}

.hero {
  min-height: 320px;
  background-size: cover;
  background-position: center;
}

.hero-mask {
  min-height: 320px;
  padding: 46px;
  background: linear-gradient(90deg, rgba(12, 24, 42, 0.76), rgba(12, 24, 42, 0.16));
  color: #fff;
}

.eyebrow {
  font-size: 12px;
  font-weight: 700;
}

h1 {
  max-width: 620px;
  margin: 12px 0;
  font-size: 34px;
  line-height: 44px;
}

p {
  max-width: 620px;
  margin: 0;
  line-height: 24px;
}

.search-card {
  min-height: 86px;
  display: grid;
  grid-template-columns: repeat(4, 1fr) 128px;
  gap: 12px;
  align-items: center;
  padding: 18px;
  background: #fff;
  border: 1px solid #e8ecf2;
}

.search-card span,
.room-card span {
  display: block;
  margin-bottom: 6px;
  color: #687386;
  font-size: 12px;
}

.search-card button {
  height: 44px;
  border: 0;
  border-radius: 8px;
  background: #142033;
  color: #fff;
}

.rooms,
.benefits {
  padding: 22px;
  background: #fff;
}

.section-title {
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 700;
  color: #202532;
}

.room-grid,
.benefit-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.room-card,
.benefit-card {
  border: 1px solid #edf1f6;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.room-card:hover,
.benefit-card:hover {
  border-color: #b8d8ff;
  box-shadow: 0 8px 18px rgba(31, 42, 61, 0.08);
}

.room-image {
  height: 138px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  margin-bottom: 12px;
}

.room-card strong,
.room-card b {
  display: block;
}

.room-card b {
  margin-top: 8px;
  color: #d64545;
  font-size: 18px;
}

.benefit-card {
  min-height: 62px;
  display: flex;
  align-items: center;
  background: #fbfcfe;
  color: #202532;
  font-weight: 600;
}

.prop-form {
  margin-bottom: 12px;
}

.prop-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.prop-actions .el-button:last-child {
  grid-column: span 2;
}

@media (max-width: 1200px) {
  .editor-shell {
    grid-template-columns: 1fr;
  }

  .search-card,
  .room-grid,
  .benefit-grid {
    grid-template-columns: 1fr;
  }
}
</style>
